import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useRef } from "react";
import { Editor } from "@tinymce/tinymce-react";
import PropTypes from "prop-types";
import { v4 } from "uuid";
import useTinyMCEAutocomplete from "../../../Hooks/useTinyMCEAutocomplete.jsx";

const imgAttrs = [
  `class`,
  `src`,
  `border=0`,
  `alt`,
  `title`,
  `hspace`,
  `vspace`,
  `width`,
  `height`,
  `align`,
  `onmouseover`,
  `onmouseout`,
  `name`,
  `image-type`,
  `object-id`,
];

export const ChatEditor = forwardRef(
  ({ onChange, directionality, placeholder, autoFocus, onImagesChange, members, ...rest }, ref) => {
    const autocompleteConfig = useTinyMCEAutocomplete({
      members,
      hideProject: true,
      hideEveryone: true,
      hideHere: true,
      allowedDelimiters: ["@"],
    });
    const innerRef = useRef(null);
    const tinymceId = useMemo(() => `tinymce_${v4()}`, []);
    const tinymceToolbarId = `${tinymceId}_toolbar`;

    const forceFocus = useCallback((editor) => {
      editor?.focus();
      editor?.selection?.select(editor?.getBody(), true);
      editor?.selection?.collapse(false);
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        focus() {
          forceFocus(innerRef.current);
        },
        getContent(options) {
          return innerRef.current?.getContent(options);
        },
      }),
      [forceFocus]
    );

    const plugins = useMemo(() => {
      const result = [`a51image`, `a51noneditable`, `a51code`, `a51paste`];
      if (members?.length > 2) {
        result.push("a51autocomplete");
      }
      return result;
    }, [members?.length]);

    const config = {
      placeholder,
      forced_root_block: "p",
      icons: "a51icons",
      skin: false,
      contextmenu: false,
      link_title: false,
      target_list: false,
      plugins: plugins.join(` `),
      menubar: false,
      br_in_pre: false,
      toolbar: false,
      toolbar_persist: true,
      fixed_toolbar_container: `#${tinymceToolbarId}`,
      statusbar: false,
      inline: true,
      object_resizing: false,
      extended_valid_elements: `img[${imgAttrs.join(`|`)}]`,
      relative_urls: false,
      directionality,
      browser_spellcheck: true,
      a51noneditable_selectors: [`.mention`, `.new_mention`, `.hashtag`, `.new_hashtag`],
      text_patterns: [
        { start: "**", end: "**", format: "bold" },
        { start: "*", end: "*", format: "italic" },
        { start: "_", end: "_", format: "underline" },
        { start: "`", end: "`", format: "code" },
      ],
      paste_block_drop: true,
      paste_as_text: true,
      a51image: {
        upload: (files) => {
          if (files.length) {
            const arrFiles = Array.from(files);
            typeof onImagesChange === "function" && onImagesChange(arrFiles);
          }
        },
        paste_images: true,
      },
      a51autocomplete: autocompleteConfig,
    };

    const handleEditorChange = useCallback(
      (_evnt, editor) => {
        if (typeof onChange === "function") {
          onChange(editor.getContent());
        }
      },
      [onChange]
    );

    const handleInit = useCallback(
      (_evnt, editor) => {
        if (autoFocus) {
          forceFocus(editor);
        }
        innerRef.current = editor;
      },
      [autoFocus, forceFocus]
    );

    return (
      <div className="chat-editor tw-w-full tw-mr-2">
        <div
          id={tinymceToolbarId}
          className="chat-editor-toolbar"
        />
        <Editor
          id={tinymceId}
          init={config}
          onEditorChange={handleEditorChange}
          onInit={handleInit}
          {...rest}
        />
      </div>
    );
  }
);

ChatEditor.propTypes = {
  placeholder: PropTypes.string,
  autoFocus: PropTypes.bool,
  directionality: PropTypes.string,
  onChange: PropTypes.func,
  onImagesChange: PropTypes.func,
  members: PropTypes.arrayOf(PropTypes.number),
};

ChatEditor.defaultProps = {
  placeholder: ``,
  directionality: `ltr`,
  autoFocus: false,
  members: [],
};

ChatEditor.displayName = "ChatEditor";
